// Gentle kitchen-timer chime, synthesized with the Web Audio API (no audio
// files). One shared chime per screen, however many timers or widgets are
// ringing; each caller registers a reason and the chime plays while any
// reason is active, for at most ALARM_MAX_MS.

import { ALARM_MAX_MS } from "../../../shared/kitchen/timers";

type Ctor = typeof AudioContext;

let ctx: AudioContext | null = null;
let loop: number | null = null;
let stopAt = 0;
let unlocked = false;
const reasons = new Map<string, number>(); // reason → ring-until (ms epoch)
const listeners = new Set<() => void>();

function getCtx(): AudioContext | null {
  if (ctx) return ctx;
  const C: Ctor | undefined = typeof window !== "undefined" ? (window.AudioContext ?? (window as unknown as { webkitAudioContext?: Ctor }).webkitAudioContext) : undefined;
  if (!C) return null;
  try {
    ctx = new C();
  } catch {
    ctx = null;
  }
  return ctx;
}

/** Browsers only allow sound after a user gesture; any tap on the page unlocks it for the session. */
function unlock() {
  const c = getCtx();
  if (!c) return;
  void c.resume().then(
    () => {
      markRunning(c);
      // Idle contexts cost CPU on a Pi; resuming later is allowed once the page has had a gesture.
      if (!reasons.size) void c.suspend().catch(() => undefined);
    },
    () => undefined,
  );
}

function markRunning(c: AudioContext) {
  if (c.state !== "running" || unlocked) return;
  unlocked = true;
  window.removeEventListener("pointerdown", unlock, { capture: true });
  window.removeEventListener("keydown", unlock, { capture: true });
  notify();
}
if (typeof window !== "undefined") {
  window.addEventListener("pointerdown", unlock, { capture: true, passive: true });
  window.addEventListener("keydown", unlock, { capture: true });
}

function notify() {
  for (const l of listeners) l();
}

/** A timer is ringing right now (a sleeping wall wakes for it). */
export function alarmRinging(): boolean {
  const now = Date.now();
  for (const until of reasons.values()) if (until > now) return true;
  return false;
}

/** True when a chime would currently be silent because the browser hasn't allowed audio yet. */
export function audioBlocked(): boolean {
  return !unlocked;
}

export function onAudioStateChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** One soft bell note: a sine with a quiet octave partial and a long exponential decay. */
function note(c: AudioContext, freq: number, at: number, peak = 0.18) {
  const gain = c.createGain();
  gain.gain.setValueAtTime(0.0001, at);
  gain.gain.exponentialRampToValueAtTime(peak, at + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + 1.4);
  gain.connect(c.destination);
  for (const [mult, level, type] of [
    [1, 1, "sine"],
    [2, 0.18, "sine"],
    [3, 0.05, "triangle"],
  ] as const) {
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq * mult, at);
    g.gain.value = level;
    osc.connect(g).connect(gain);
    osc.start(at);
    osc.stop(at + 1.5);
  }
}

function chime() {
  const c = getCtx();
  if (!c || c.state !== "running") return;
  const t = c.currentTime + 0.05;
  // A rising three-note figure (E5, G#5, B5): clear but not harsh.
  note(c, 659.25, t);
  note(c, 830.61, t + 0.22);
  note(c, 987.77, t + 0.44, 0.14);
}

function tick() {
  const now = Date.now();
  for (const [k, until] of reasons) if (until <= now) reasons.delete(k);
  if (!reasons.size || now >= stopAt) {
    stop();
    return;
  }
  chime();
}

function stop() {
  if (loop !== null) {
    clearInterval(loop);
    loop = null;
    if (ctx && unlocked) void ctx.suspend().catch(() => undefined);
  }
}

/**
 * Ask for the chime to play for `reason` until `until` (capped at 2 minutes
 * from now), or pass null to withdraw the request.
 */
export function setAlarm(reason: string, until: number | null) {
  if (until === null) {
    reasons.delete(reason);
    if (!reasons.size) stop();
    return;
  }
  const cap = Date.now() + ALARM_MAX_MS;
  reasons.set(reason, Math.min(until, cap));
  stopAt = Math.max(...reasons.values());
  if (loop === null) {
    const c = getCtx();
    loop = window.setInterval(tick, 2600);
    if (c && c.state !== "running") {
      void c.resume().then(
        () => {
          markRunning(c);
          if (loop !== null) chime();
        },
        () => undefined,
      );
    } else if (c) {
      markRunning(c);
      chime();
    }
  }
}
